import { currentIdToken, currentToken } from "./auth";
import { config } from "./config";
import type {
  AgentCostRow,
  ApiKeyInfo,
  Artifact,
  AuditEntry,
  DirectoryUser,
  FactoryLayout,
  FactoryState,
  InboxCommentsPage,
  InboxResponse,
  InspectionMode,
  IssueActivity,
  IssueAttachment,
  IssueComment,
  IssueDetail,
  IssuePage,
  IssueSettings,
  IssueStatusInfo,
  IssuesMeta,
  IssueTypeInfo,
  LabelInfo,
  LatestRelease,
  LoopBudget,
  McpServerView,
  MergeQueueItem,
  Preview,
  PreviewEvent,
  PreviewSummary,
  RelationType,
  ReleaseDetail,
  ReleaseTarget,
  ReleaseView,
  RepoView,
  Run,
  RunEvent,
  RunHistoryItem,
  RunListItem,
  RunSort,
  RunStationSummary,
  SearchResults,
  SlaPolicyInfo,
  SnapshotRow,
  Task,
  TaskListItem,
  TaskSort,
  TeamView,
  TestStepOutcome,
  TestStepResult,
  TypePeriodRow,
  UserRole,
  WeeklyAnalyticsRow,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = currentToken();
  const response = await fetch(`${config.apiUrl}${path}`, {
    ...init,
    headers: {
      accept: "application/json",
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(currentIdToken() ? { "x-id-token": currentIdToken()! } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  const text = await response.text();
  const body = text ? safeParse(text) : undefined;
  if (!response.ok) {
    const fromBody =
      body && typeof body === "object" && "message" in body
        ? String((body as { message: unknown }).message)
        : "";
    throw new ApiError(fromBody || `HTTP ${response.status}`, response.status, body);
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Which runs `/runs` and `/runs/stations` cover. List values are comma separated. */
export interface RunListParams {
  station?: string;
  status?: string;
  provider?: string;
  verdict?: string;
  lane?: string;
  q?: string;
  createdAfter?: string;
}

function searchOf(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  return search.toString();
}

export const api = {
  health: () => request<{ ok: boolean; stage: string }>("/health"),
  me: () => request<{ email: string; groups: string[]; role: UserRole }>("/me"),
  layout: () => request<FactoryLayout>("/factory/layout"),
  state: () => request<FactoryState>("/factory/state"),
  tasks: (
    params: {
      status?: string;
      lane?: string;
      station?: string;
      q?: string;
      sort?: TaskSort;
      direction?: "asc" | "desc";
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") search.set(key, String(value));
    }
    return request<{ tasks: TaskListItem[] }>(`/tasks?${search.toString()}`);
  },
  task: (id: string) =>
    request<{
      task: Task;
      runs: RunListItem[];
      currentRun: Run | null;
      artifacts: Artifact[];
      mergeQueue: MergeQueueItem | null;
      loopBudget: LoopBudget;
    }>(`/tasks/${id}`),
  createTask: (input: {
    title: string;
    description?: string;
    startAgentId?: string;
    lane?: string;
    provider?: string;
    instructions?: string;
    /** A Settings repo key. Wins over `repositoryUrl`. */
    repoKey?: string;
    repositoryUrl?: string;
    baseRef?: string;
  }) => request<{ task: Task; run: Run }>("/tasks", { method: "POST", body: JSON.stringify(input) }),
  cancelTask: (id: string) => request<{ ok: boolean }>(`/tasks/${id}/cancel`, { method: "POST" }),
  retryTask: (id: string, input: { agentId?: string; provider?: string }) =>
    request<{ run: Run }>(`/tasks/${id}/retry`, { method: "POST", body: JSON.stringify(input) }),
  resumeTask: (id: string, action: "rework" | "continue") =>
    request<{ run: Run | null }>(`/tasks/${id}/resume`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  resumeIssue: (ref: string, action: "rework" | "continue") =>
    request<{ run: Run | null }>(`/issues/${encodeURIComponent(ref)}/resume`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  /** Admin only. A work order held for its spent budget goes back to the Fixer. */
  grantTaskLoops: (id: string, input: { add: number; reason?: string }) =>
    request<{ loopBudget: LoopBudget; run: Run | null }>(`/tasks/${id}/loop-budget`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  grantIssueLoops: (ref: string, input: { add: number; reason?: string }) =>
    request<{ loopBudget: LoopBudget; run: Run | null }>(`/issues/${encodeURIComponent(ref)}/loop-budget`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  previews: () => request<{ previews: Preview[] }>("/previews"),
  preview: (id: string) => request<{ preview: Preview; events: PreviewEvent[] }>(`/previews/${id}`),
  createPreview: (input: { prUrl: string; ttlMinutes?: number }) =>
    request<{ preview: Preview }>("/previews", { method: "POST", body: JSON.stringify(input) }),
  stopPreview: (id: string) => request<{ preview: Preview }>(`/previews/${id}/stop`, { method: "POST" }),
  restartPreview: (id: string) =>
    request<{ preview: Preview }>(`/previews/${id}/restart`, { method: "POST" }),
  extendPreview: (id: string, minutes: number) =>
    request<{ preview: Preview }>(`/previews/${id}/extend`, {
      method: "POST",
      body: JSON.stringify({ minutes }),
    }),
  createIssuePreview: (ref: string) =>
    request<{ preview: PreviewSummary }>(`/issues/${encodeURIComponent(ref)}/preview`, { method: "POST" }),
  signOffIssue: (ref: string) =>
    request<{ item: MergeQueueItem }>(`/issues/${encodeURIComponent(ref)}/sign-off`, { method: "POST" }),
  requestIssueChanges: (ref: string, feedback: string) =>
    request<{ ok: boolean }>(`/issues/${encodeURIComponent(ref)}/request-changes`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),
  cancelIssue: (ref: string) =>
    request<{ issue: IssueDetail }>(`/issues/${encodeURIComponent(ref)}/cancel`, { method: "POST" }),
  setTestStepResult: (ref: string, stepId: string, body: { result: TestStepOutcome; note?: string }) =>
    request<{ result: TestStepResult }>(
      `/issues/${encodeURIComponent(ref)}/test-steps/${encodeURIComponent(stepId)}/result`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  clearTestStepResult: (ref: string, stepId: string) =>
    request<{ ok: boolean }>(
      `/issues/${encodeURIComponent(ref)}/test-steps/${encodeURIComponent(stepId)}/result`,
      { method: "DELETE" },
    ),
  // ------------------------------------------------------------ releases

  releases: (params: { status?: string; kind?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set("status", params.status);
    if (params.kind) search.set("kind", params.kind);
    if (params.limit) search.set("limit", String(params.limit));
    return request<{ releases: ReleaseView[] }>(`/releases?${search.toString()}`);
  },
  latestRelease: () => request<{ latest: LatestRelease | null }>("/releases/latest"),
  release: (id: string) => request<ReleaseDetail>(`/releases/${id}`),
  createRelease: (input: {
    kind: "standard" | "urgent" | "scheduled";
    scheduledFor?: string;
    fromLastRelease?: boolean;
    issueIds?: string[];
  }) => request<ReleaseDetail>("/releases", { method: "POST", body: JSON.stringify(input) }),
  shipRelease: (id: string, tag?: string) =>
    request<ReleaseDetail>(`/releases/${id}/ship`, {
      method: "POST",
      body: JSON.stringify(tag ? { tag } : {}),
    }),
  cancelRelease: (id: string) => request<ReleaseDetail>(`/releases/${id}/cancel`, { method: "POST" }),
  retryRelease: (id: string) => request<ReleaseDetail>(`/releases/${id}/retry`, { method: "POST" }),
  syncRelease: (id: string) => request<ReleaseDetail>(`/releases/${id}/sync`, { method: "POST" }),
  regenerateReleaseNotes: (id: string) => request<ReleaseDetail>(`/releases/${id}/notes`, { method: "POST" }),
  notifyReleaseTeams: (id: string) => request<ReleaseDetail>(`/releases/${id}/notify`, { method: "POST" }),
  setIssueRelease: (ref: string, target: ReleaseTarget) =>
    request<{ issue: IssueDetail; release: ReleaseDetail | null }>(
      `/issues/${encodeURIComponent(ref)}/release`,
      {
        method: "POST",
        body: JSON.stringify(target),
      },
    ),

  runs: (
    params: RunListParams & { sort?: RunSort; direction?: "asc" | "desc"; limit?: number; offset?: number },
  ) => request<{ runs: RunHistoryItem[] }>(`/runs?${searchOf(params)}`),
  runStations: (params: RunListParams) =>
    request<{ stations: RunStationSummary[] }>(`/runs/stations?${searchOf(params)}`),
  run: (id: string) => request<{ run: Run }>(`/runs/${id}`),
  runEvents: (id: string, after = 0) => request<{ events: RunEvent[] }>(`/runs/${id}/events?after=${after}`),
  followUp: (id: string, message: string) =>
    request<{ run: Run }>(`/runs/${id}/follow-up`, { method: "POST", body: JSON.stringify({ message }) }),
  cancelRun: (id: string) => request<{ run: Run }>(`/runs/${id}/cancel`, { method: "POST" }),
  mergeQueue: (all = false) => request<{ items: MergeQueueItem[] }>(`/merge-queue${all ? "?all=1" : ""}`),
  approveMerge: (id: string) =>
    request<{ item: MergeQueueItem }>(`/merge-queue/${id}/approve`, { method: "POST" }),
  markMergeReady: (id: string) =>
    request<{ item: MergeQueueItem }>(`/merge-queue/${id}/ready`, { method: "POST" }),
  approveAndMerge: (id: string) =>
    request<{ item: MergeQueueItem }>(`/merge-queue/${id}/approve-and-merge`, { method: "POST" }),
  mcpServers: () => request<{ servers: McpServerView[] }>("/mcp/servers"),
  mcpEnable: (id: string, enabled: boolean) =>
    request<McpServerView>(`/mcp/servers/${id}/${enabled ? "enable" : "disable"}`, { method: "POST" }),
  mcpCredentials: (id: string, body: { token: string } | { env: Record<string, string> }) =>
    request<McpServerView>(`/mcp/servers/${id}/credentials`, { method: "POST", body: JSON.stringify(body) }),
  mcpDisconnect: (id: string) => request<McpServerView>(`/mcp/servers/${id}/disconnect`, { method: "POST" }),
  mcpTest: (id: string) => request<McpServerView>(`/mcp/servers/${id}/test`, { method: "POST" }),
  mcpTools: (id: string, tools: string[] | null) =>
    request<McpServerView>(`/mcp/servers/${id}/tools`, { method: "PUT", body: JSON.stringify({ tools }) }),
  mcpAttachments: (id: string, attachments: { sections: string[]; agents: string[] }) =>
    request<McpServerView>(`/mcp/servers/${id}/attachments`, {
      method: "PUT",
      body: JSON.stringify(attachments),
    }),
  mcpOauthStart: (id: string) =>
    request<{ url: string }>(
      `/mcp/servers/${id}/oauth/start?returnTo=${encodeURIComponent(`${window.location.origin}/integrations`)}`,
    ),
  audit: (limit = 100) => request<{ entries: AuditEntry[] }>(`/audit?limit=${limit}`),

  // -------------------------------------------------------------- issues

  issues: (params: Record<string, string>) =>
    request<IssuePage>(`/issues?${new URLSearchParams(params).toString()}`),
  /** One count per view, in order. Each view takes the filters `issues` does. */
  issueCounts: (views: Array<Record<string, string>>) =>
    request<{ counts: number[] }>(
      `/issues/counts?${views.map((view) => `view=${encodeURIComponent(new URLSearchParams(view).toString())}`).join("&")}`,
    ),
  issuesMeta: () => request<IssuesMeta>("/issues/meta"),
  search: (q: string, signal?: AbortSignal) =>
    request<SearchResults>(`/search?${new URLSearchParams({ q }).toString()}`, { signal }),
  issue: (ref: string) => request<{ issue: IssueDetail }>(`/issues/${encodeURIComponent(ref)}`),
  voiceSession: (sdp: string, signal?: AbortSignal) =>
    request<{ session: { id: string }; transport: { type: "webrtc"; sdp: string } }>(
      "/issues/voice/session",
      {
        method: "POST",
        body: JSON.stringify({ sdp }),
        signal,
      },
    ),
  voiceDraft: (transcript: string) =>
    request<{ title: string; description: string; typeKey: string; priority: number }>(
      "/issues/voice/draft",
      {
        method: "POST",
        body: JSON.stringify({ transcript }),
      },
    ),
  createIssue: (input: {
    title: string;
    description?: string;
    typeKey: string;
    statusKey?: string;
    /** Repo key; omitted = the parent's repo for a sub-issue, else the default repo. */
    repoKey?: string | null;
    priority?: number;
    storyPoints?: number | null;
    reviewerId?: string | null;
    reporterId?: string;
    teamIds?: string[];
    labelIds?: string[];
    parent?: string | null;
    sla?: string | null;
  }) => request<{ issue: IssueDetail }>("/issues", { method: "POST", body: JSON.stringify(input) }),
  updateIssue: (
    ref: string,
    input: Partial<{
      title: string;
      description: string;
      testingInstructions: string;
      typeKey: string;
      statusKey: string;
      repoKey: string;
      priority: number;
      storyPoints: number | null;
      reviewerId: string | null;
      reporterId: string | null;
      prUrl: string | null;
      previewRequired: boolean | null;
      sla: string | null;
      parent: string | null;
      teamIds: string[];
      labelIds: string[];
    }>,
  ) =>
    request<{ issue: IssueDetail }>(`/issues/${encodeURIComponent(ref)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  issueTeamPreview: (params: { reporterId?: string; reviewerId?: string }) => {
    const query = new URLSearchParams();
    if (params.reporterId) query.set("reporterId", params.reporterId);
    if (params.reviewerId) query.set("reviewerId", params.reviewerId);
    return request<{ teamIds: string[] }>(`/issues/team-preview?${query.toString()}`);
  },
  issueActivity: (ref: string) =>
    request<{ activity: IssueActivity[] }>(`/issues/${encodeURIComponent(ref)}/activity`),
  setIssueArchived: (ref: string, archived: boolean) =>
    request<{ issue: IssueDetail }>(
      `/issues/${encodeURIComponent(ref)}/${archived ? "archive" : "unarchive"}`,
      { method: "POST" },
    ),
  delegateIssue: (ref: string, input?: { instructions?: string | null; startAgentId?: string | null }) =>
    request<{ ok: boolean }>(`/issues/${encodeURIComponent(ref)}/delegate`, {
      method: "POST",
      body: JSON.stringify({
        instructions: input?.instructions ?? null,
        startAgentId: input?.startAgentId ?? null,
      }),
    }),
  addRelation: (ref: string, related: string, type: RelationType) =>
    request<{ issue: IssueDetail }>(`/issues/${encodeURIComponent(ref)}/relations`, {
      method: "POST",
      body: JSON.stringify({ related, type }),
    }),
  removeRelation: (ref: string, related: string, type: RelationType) =>
    request<{ ok: boolean }>(`/issues/${encodeURIComponent(ref)}/relations`, {
      method: "DELETE",
      body: JSON.stringify({ related, type }),
    }),
  issueLabels: (ref: string, body: { add?: string[]; remove?: string[] }) =>
    request<{ issue: IssueDetail }>(`/issues/${encodeURIComponent(ref)}/labels`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  issueComments: (ref: string) =>
    request<{ comments: IssueComment[] }>(`/issues/${encodeURIComponent(ref)}/comments`),
  addComment: (ref: string, body: { body: string; parentId?: string | null; attachmentIds?: string[] }) =>
    request<{ comment: IssueComment }>(`/issues/${encodeURIComponent(ref)}/comments`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateComment: (id: string, body: string) =>
    request<{ comment: IssueComment }>(`/comments/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    }),
  deleteComment: (id: string) => request<{ ok: boolean }>(`/comments/${id}`, { method: "DELETE" }),
  inbox: () => request<InboxResponse>("/inbox"),
  inboxComments: (params: { limit: number; offset: number }) => {
    const search = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
    });
    return request<InboxCommentsPage>(`/inbox/comments?${search.toString()}`);
  },
  markInboxSeen: (ref: string) =>
    request<{ ok: boolean }>("/inbox/seen", {
      method: "POST",
      body: JSON.stringify({ ref }),
    }),
  createAttachment: (ref: string, input: { fileName: string; contentType: string; sizeBytes: number }) =>
    request<{ attachment: IssueAttachment; uploadUrl: string }>(
      `/issues/${encodeURIComponent(ref)}/attachments`,
      { method: "POST", body: JSON.stringify(input) },
    ),
  completeAttachment: (id: string) =>
    request<{ attachment: IssueAttachment }>(`/attachments/${id}/complete`, { method: "POST" }),
  attachmentUrl: (id: string) => request<{ url: string }>(`/attachments/${id}/url`),
  analyticsWeekly: (weeks: number, type?: string) =>
    request<{ weeks: number; type: string | null; rows: WeeklyAnalyticsRow[] }>(
      `/analytics/weekly?weeks=${weeks}${type ? `&type=${encodeURIComponent(type)}` : ""}`,
    ),
  analyticsTypes: (weeks: number, type?: string) =>
    request<{ weeks: number; type: string | null; periodStart: string; rows: TypePeriodRow[] }>(
      `/analytics/types?weeks=${weeks}${type ? `&type=${encodeURIComponent(type)}` : ""}`,
    ),
  analyticsSnapshot: () => request<{ rows: SnapshotRow[] }>("/analytics/snapshot"),
  analyticsAgents: () => request<{ rows: AgentCostRow[] }>("/analytics/agents"),

  // --------------------------------------------------------------- teams

  teams: () => request<{ teams: TeamView[] }>("/teams"),
  team: (idOrKey: string) => request<{ team: TeamView }>(`/teams/${encodeURIComponent(idOrKey)}`),
  createTeam: (input: {
    key: string;
    name: string;
    description?: string;
    color?: string;
    slackChannel?: string | null;
  }) => request<{ team: TeamView }>("/teams", { method: "POST", body: JSON.stringify(input) }),
  updateTeam: (
    id: string,
    input: Partial<{ name: string; description: string; color: string; slackChannel: string | null }>,
  ) => request<{ team: TeamView }>(`/teams/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteTeam: (id: string) => request<{ ok: boolean }>(`/teams/${id}`, { method: "DELETE" }),
  setTeamMembers: (id: string, members: Array<{ userId: string; role?: "approver" | "member" }>) =>
    request<{ team: TeamView }>(`/teams/${id}/members`, {
      method: "PUT",
      body: JSON.stringify({ members }),
    }),

  // --------------------------------------------------------------- repos

  repos: () => request<{ repos: RepoView[] }>("/repos"),
  repo: (key: string) => request<{ repo: RepoView }>(`/repos/${encodeURIComponent(key)}`),
  createRepo: (input: { key: string; name: string; url: string; defaultBranch?: string }) =>
    request<{ repo: RepoView }>("/repos", { method: "POST", body: JSON.stringify(input) }),
  updateRepo: (key: string, input: Partial<{ name: string; url: string; defaultBranch: string }>) =>
    request<{ repo: RepoView }>(`/repos/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  setRepoActive: (key: string, active: boolean) =>
    request<{ repo: RepoView }>(`/repos/${encodeURIComponent(key)}/active`, {
      method: "PUT",
      body: JSON.stringify({ active }),
    }),
  setRepoDefault: (key: string) =>
    request<{ repo: RepoView }>(`/repos/${encodeURIComponent(key)}/default`, { method: "PUT" }),
  deleteRepo: (key: string) =>
    request<{ ok: boolean }>(`/repos/${encodeURIComponent(key)}`, { method: "DELETE" }),
  // ----------------------------------------------------- directory + settings

  directoryUsers: (all = false) =>
    request<{ users: DirectoryUser[] }>(`/directory/users${all ? "?all=1" : ""}`),
  setUserActive: (id: string, active: boolean) =>
    request<{ user: DirectoryUser }>(`/directory/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    }),
  setUserRole: (id: string, role: UserRole) =>
    request<{ user: DirectoryUser }>(`/directory/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  directorySync: () =>
    request<{
      ok: boolean;
      created: number;
      skipped: number;
      error?: string;
      slack?: { ok: boolean; matched: number; error?: string };
    }>("/directory/sync", {
      method: "POST",
    }),
  settingsLabels: () => request<{ labels: LabelInfo[] }>("/settings/labels"),
  createLabel: (input: { name: string; color?: string }) =>
    request<{ label: LabelInfo }>("/settings/labels", { method: "POST", body: JSON.stringify(input) }),
  updateLabel: (id: string, input: Partial<{ name: string; color: string }>) =>
    request<{ label: LabelInfo }>(`/settings/labels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteLabel: (id: string) => request<{ ok: boolean }>(`/settings/labels/${id}`, { method: "DELETE" }),
  settingsTypes: () => request<{ types: IssueTypeInfo[] }>("/settings/types"),
  createType: (input: {
    key: string;
    name: string;
    identifierPrefix?: string;
    icon?: string;
    color?: string;
  }) => request<{ type: IssueTypeInfo }>("/settings/types", { method: "POST", body: JSON.stringify(input) }),
  updateType: (
    key: string,
    input: Partial<{ name: string; identifierPrefix: string; icon: string; color: string }>,
  ) =>
    request<{ type: IssueTypeInfo }>(`/settings/types/${key}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  deleteType: (key: string) => request<{ ok: boolean }>(`/settings/types/${key}`, { method: "DELETE" }),
  settingsStatuses: () => request<{ statuses: IssueStatusInfo[] }>("/settings/statuses"),
  updateStatus: (key: string, input: Partial<{ name: string; color: string; position: number }>) =>
    request<{ status: IssueStatusInfo }>(`/settings/statuses/${key}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  slaPolicies: () => request<{ policies: SlaPolicyInfo[] }>("/settings/sla-policies"),
  createSlaPolicy: (input: {
    name: string;
    issueTypeKey?: string | null;
    priority?: number | null;
    sla: string;
  }) =>
    request<{ policy: SlaPolicyInfo }>("/settings/sla-policies", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteSlaPolicy: (id: string) =>
    request<{ ok: boolean }>(`/settings/sla-policies/${id}`, { method: "DELETE" }),
  setInspectionMode: (mode: InspectionMode) =>
    request<{ mode: InspectionMode }>("/settings/inspection", {
      method: "PUT",
      body: JSON.stringify({ mode }),
    }),
  issueSettings: () => request<IssueSettings>("/settings/values"),
  updateIssueSettings: (input: Partial<IssueSettings>) =>
    request<IssueSettings>("/settings/values", { method: "PUT", body: JSON.stringify(input) }),
  apiKeys: () => request<{ keys: ApiKeyInfo[] }>("/api-keys"),
  createApiKey: (name: string) =>
    request<{ key: string; row: ApiKeyInfo }>("/api-keys", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  revokeApiKey: (id: string) => request<{ ok: boolean }>(`/api-keys/${id}`, { method: "DELETE" }),
  trackerDeliveries: (limit = 50) =>
    request<{
      deliveries: Array<{
        id: string;
        provider: string;
        type: string;
        action: string;
        processedAt: string | null;
        error: string | null;
        createdAt: string;
      }>;
    }>(`/events/tracker?limit=${limit}`),
};
